import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DefaultLocalMixService } from '@asm/features/sync/services/mix/default-local-mix.service';

/**
 * Handler offline para endpoints de mixes.
 *
 * Cobre:
 * - GET  /mix                       → lista paginada de mixes (página de mixes)
 * - GET  /mix/select                → todos os mixes sem paginação (selects/filtros)
 * - GET  /api/mixes                 → alias legado para todos os mixes
 * - POST /mix/calculate-adherence   → cálculo de aderência ao mix (modal de revisão)
 */
@Injectable({ providedIn: 'root' })
export class MixOfflineHandler {
  private readonly mixService = inject(DefaultLocalMixService);

  canHandle(req: HttpRequest<unknown>): boolean {
    const { url, method } = req;
    if (method === 'POST' && url.includes('/mix/calculate-adherence')) return true;
    return method === 'GET' && (/\/mix(\?|$)/.test(url) || url.includes('/mix/select') || url.includes('/api/mixes'));
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const { url, method } = req;

    if (method === 'POST' && url.includes('/mix/calculate-adherence')) {
      return this.handleCalculateAdherence(req);
    }

    if (method === 'GET' && /\/mix(\?|$)/.test(url)) {
      return this.handlePaginatedMixes(req);
    }

    if (method === 'GET' && (url.includes('/mix/select') || url.includes('/api/mixes'))) {
      return new HttpResponse({
        status: 200,
        body: await this.mixService.getAllMixes(),
      });
    }

    throw new Error(`[MixOfflineHandler] Rota não mapeada: ${method} ${url}`);
  }

  private async handleCalculateAdherence(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const body = req.body as { productCodes?: string[]; b2bUnitUid?: string } | null;
    const productCodes = body?.productCodes ?? [];
    const adherences = await this.mixService.calculateAdherence(productCodes);
    return new HttpResponse({ status: 200, body: adherences });
  }

  private async handlePaginatedMixes(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const page = Number(req.params.get('page') ?? '1');
    const pageSize = Number(req.params.get('pageSize') ?? '12');
    const safePage = Number.isNaN(page) || page < 1 ? 1 : page;
    const safePageSize = Number.isNaN(pageSize) || pageSize < 1 ? 12 : pageSize;

    const body = await this.mixService.getPaginatedMixes(safePage, safePageSize);
    return new HttpResponse({ status: 200, body });
  }
}
